import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { startAudit as startAuditApi } from "../../services/audit.api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../Auth/AuthContext";

const STORAGE_KEY = "auditModeTarget";

const AuditModeContext = createContext({
  auditActive: false,
  auditTarget: null,
  startAudit: async () => {},
  exitAudit: () => {},
});

export const AuditModeProvider = ({ children }) => {
  const realAuthCtx = useContext(AuthContext);

  const [auditTarget, setAuditTarget] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const target = JSON.parse(stored);
        // Restaurar header axios en recarga de página
        api.defaults.headers.common["x-audit-target-id"] = String(target.id);
        return target;
      }
    } catch { /* */ }
    return null;
  });

  const auditActive = auditTarget !== null;

  const startAudit = useCallback(async (user) => {
    try {
      const { data } = await startAuditApi(user.id);
      const target = { id: data.id, name: data.name, profile: data.profile };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
      api.defaults.headers.common["x-audit-target-id"] = String(data.id);
      setAuditTarget(target);
      toast.info(`Modo Auditoría activado — Visualizando panel de ${data.name}`);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const exitAudit = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    delete api.defaults.headers.common["x-audit-target-id"];
    setAuditTarget(null);
    toast.success("Modo Auditoría desactivado — Regresaste a tu panel de administrador.");
  }, []);

  // Shadow AuthContext: solo muta profile para que sidebar y guardas de UI
  // vean el perfil del usuario auditado sin alterar id, queues ni token.
  const effectiveAuthCtx = useMemo(() => {
    if (!auditActive || !auditTarget) return realAuthCtx;
    return {
      ...realAuthCtx,
      user: { ...realAuthCtx.user, profile: auditTarget.profile },
    };
  }, [realAuthCtx, auditActive, auditTarget]);

  return (
    <AuditModeContext.Provider value={{ auditActive, auditTarget, startAudit, exitAudit }}>
      <AuthContext.Provider value={effectiveAuthCtx}>
        {children}
      </AuthContext.Provider>
    </AuditModeContext.Provider>
  );
};

export const useAuditMode = () => useContext(AuditModeContext);

export default AuditModeContext;
